import React from 'react';
import { 
  FiPackage, 
  FiPieChart, 
  FiLayers,
  FiAlertTriangle, 
  FiAlertCircle 
} from 'react-icons/fi';

const InventoryStats = ({ inventory = [], serverStats = null }) => {
  const items = Array.isArray(inventory) ? inventory : [];

  // Local fallback metrics
  const localStockValuation = items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.selling_price) || 0;
    return sum + (qty * price);
  }, 0);

  const localStockCost = items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const cost = parseFloat(item.cost_price) || 0;
    return sum + (qty * cost);
  }, 0);

  const localStockQuantity = items.reduce((sum, item) => {
    return sum + (parseFloat(item.quantity) || 0);
  }, 0);

  const localLowStock = items.filter(item => {
    const qty = parseFloat(item.quantity) || 0;
    const reorder = parseFloat(item.reorder_level) || 0;
    return qty <= reorder && qty > 0;
  }).length;

  const localOutOfStock = items.filter(item => (parseFloat(item.quantity) || 0) === 0).length;
  const localActiveItems = items.filter(item => item.status === 'active').length;

  const totalValue = serverStats?.totalValue !== undefined 
    ? serverStats.totalValue 
    : localStockValuation;

  const stockCost = serverStats?.stockCost !== undefined 
    ? serverStats.stockCost 
    : localStockCost;

  const stockQuantity = serverStats?.stockQuantity !== undefined 
    ? serverStats.stockQuantity 
    : localStockQuantity;

  const totalItems = serverStats?.totalItems !== undefined ? serverStats.totalItems : items.length;
  const lowStock = serverStats?.lowStock !== undefined ? serverStats.lowStock : localLowStock;
  const outOfStock = serverStats?.outOfStock !== undefined ? serverStats.outOfStock : localOutOfStock;
  const activeItems = serverStats?.activeItems !== undefined ? serverStats.activeItems : localActiveItems;
  const potentialMargin = totalValue > 0 ? (((totalValue - stockCost) / totalValue) * 100).toFixed(1) : '0';

  return (
    <div className="inventory-stats-container">
      <div className="inventory-stats operational-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {/* Total Products */}
        <div className="stat-card">
          <div className="stat-icon icon-items">
            <FiPackage />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Products</div>
            <h3 className="stat-number">{totalItems}</h3>
            <div className="stat-subtext">
              <span>{activeItems} Active products</span>
            </div>
          </div>
        </div>

        {/* Units on Hand */}
        <div className="stat-card">
          <div className="stat-icon icon-quantity">
            <FiLayers />
          </div>
          <div className="stat-content">
            <div className="stat-label">Units in Stock</div>
            <h3 className="stat-number">{stockQuantity}</h3>
            <div className="stat-subtext">
              <span>Total available warehouse units</span>
            </div>
          </div>
        </div>

        {/* Stock Valuation */}
        <div className="stat-card stat-valuation">
          <div className="stat-icon icon-valuation">
            <FiPieChart />
          </div>
          <div className="stat-content">
            <div className="stat-label">Stock Valuation</div>
            <h3 className="stat-number">
              ₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h3>
            <div className="stat-subtext">
              <span>Cost: ₹{stockCost.toLocaleString('en-IN')} ({potentialMargin}% potential)</span>
            </div>
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className={`stat-card ${lowStock > 0 ? 'card-warning' : ''}`}>
          <div className="stat-icon icon-warning">
            <FiAlertTriangle />
          </div>
          <div className="stat-content">
            <div className="stat-label">Low Stock Alerts</div>
            <h3 className="stat-number">{lowStock}</h3>
            <div className="stat-subtext">
              <span className={lowStock > 0 ? 'text-warning' : 'text-success'}>
                {lowStock > 0 ? 'Reorder needed soon' : 'Healthy inventory level'}
              </span>
            </div>
          </div>
        </div>

        {/* Out of Stock Alert */}
        <div className={`stat-card ${outOfStock > 0 ? 'card-danger' : ''}`}>
          <div className="stat-icon icon-danger">
            <FiAlertCircle />
          </div>
          <div className="stat-content">
            <div className="stat-label">Out of Stock</div>
            <h3 className="stat-number">{outOfStock}</h3>
            <div className="stat-subtext">
              <span className={outOfStock > 0 ? 'text-danger' : 'text-success'}>
                {outOfStock > 0 ? 'Depleted items (0 qty)' : 'Zero stockouts'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryStats;